import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useClubs } from "../hooks/useClubs";
import { useAuth } from "../hooks/useAuth";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  addEvent,
  addNotice,
  createClub,
  deleteClub,
  deleteEvent,
  deleteIssue,
  deleteNotice,
  listEvents,
  listIssues,
  listNotices,
  updateClub,
  updateEvent,
  updateIssueStatus,
  updateNotice,
} from "../hooks/useAdminActions";
import type { Club, Issue, IssueStatus, Notice, Severity } from "../types";

type Tab = "clubs" | "news" | "turniere" | "meldungen";
type EventRow = { id: string; title: string; date: number; description: string; registrationUrl?: string; active: boolean };

export function AdminDashboard() {
  const { user, logout } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const { clubs, loading: clubsLoading, refetch: refetchClubs } = useClubs();
  const [tab, setTab] = useState<Tab>("clubs");
  const [selectedClub, setSelectedClub] = useState<string>("");

  useEffect(() => {
    if (!selectedClub && clubs.length > 0) setSelectedClub(clubs[0].id);
  }, [clubs, selectedClub]);

  async function handleLogout() {
    await logout();
    navigate("/admin/login");
  }

  async function copyUid() {
    if (user?.uid) await navigator.clipboard.writeText(user.uid);
  }

  return (
    <main className="mx-auto min-h-dvh max-w-2xl px-5 pb-16 pt-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold text-turf-900">Admin-Dashboard</h1>
        <button onClick={handleLogout} className="text-sm font-medium text-ink-400 underline-offset-2 hover:underline">
          Abmelden
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-chalk-200 bg-white px-4 py-3 text-xs">
        <span className="text-ink-400">Angemeldet als</span>
        <span className="font-medium text-ink-900">{user?.email}</span>
        <span className="text-ink-400">·</span>
        <span className="text-ink-400">UID</span>
        <code className="rounded bg-chalk-100 px-1.5 py-0.5 font-mono text-ink-900">{user?.uid}</code>
        <button type="button" onClick={copyUid} className="font-medium text-turf-700 underline-offset-2 hover:underline">
          kopieren
        </button>
        <span className="text-ink-400">·</span>
        <span className={isAdmin ? "font-medium text-turf-700" : "font-medium text-flag-600"}>
          {isAdmin === null ? "prüfe Admin-Status…" : isAdmin ? "Admin-Zugriff aktiv" : "kein Admin-Zugriff (UID fehlt/falsch in 'admins')"}
        </span>
      </div>

      <div role="tablist" className="mt-6 grid grid-cols-4 gap-1 rounded-xl bg-chalk-200 p-1">
        {([
          { id: "clubs", label: "Clubs" },
          { id: "news", label: "News" },
          { id: "turniere", label: "Turniere" },
          { id: "meldungen", label: "Meldungen" },
        ] as { id: Tab; label: string }[]).map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`h-10 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id ? "bg-white text-turf-800 shadow-sm" : "text-ink-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== "clubs" && (
        <div className="mt-5">
          <label htmlFor="club-picker" className="mb-1.5 block text-sm font-medium text-ink-900">
            Club
          </label>
          <select
            id="club-picker"
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            className="h-11 w-full rounded-xl border border-chalk-200 bg-white px-3 text-base"
          >
            {clubsLoading && <option>Wird geladen…</option>}
            {clubs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mt-6">
        {tab === "clubs" && <ClubsPanel clubs={clubs} onChanged={refetchClubs} />}
        {tab === "news" && selectedClub && <NewsPanel clubId={selectedClub} />}
        {tab === "turniere" && selectedClub && <EventsPanel clubId={selectedClub} />}
        {tab === "meldungen" && selectedClub && <IssuesPanel clubId={selectedClub} />}
        {tab !== "clubs" && !selectedClub && !clubsLoading && (
          <p className="text-sm text-ink-400">Erst einen Club unter "Clubs" anlegen.</p>
        )}
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-ink-900">{label}</span>
      {children}
    </label>
  );
}

const inputClass = "h-11 w-full rounded-xl border border-chalk-200 bg-white px-3 text-base outline-none focus:border-turf-600";

function emptyClubForm() {
  return { name: "", phone: "", email: "", address: "", website: "", greenFee: "", platzreife: "", turnier: "", pricesUrl: "", eventsUrl: "", teeTimeUrl: "", restaurantHours: "", sekretariatHours: "", latitude: "", longitude: "" };
}

function ClubsPanel({ clubs, onChanged }: { clubs: Club[]; onChanged: () => void }) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyClubForm());
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function startEdit(c: Club) {
    setEditingId(c.id);
    setForm({
      name: c.name,
      phone: c.contact?.phone ?? "",
      email: c.contact?.email ?? "",
      address: c.contact?.address ?? "",
      website: c.contact?.website ?? "",
      greenFee: c.prices?.greenFee ?? "",
      platzreife: c.prices?.platzreife ?? "",
      turnier: c.prices?.turnier ?? "",
      pricesUrl: c.pricesUrl ?? "",
      eventsUrl: c.eventsUrl ?? "",
      teeTimeUrl: c.teeTimeUrl ?? "",
      restaurantHours: c.openingHours?.restaurant ?? "",
      sekretariatHours: c.openingHours?.sekretariat ?? "",
      latitude: c.location?.latitude ? String(c.location.latitude) : "",
      longitude: c.location?.longitude ? String(c.location.longitude) : "",
    });
    setMessage(null);
    setError(null);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyClubForm());
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);
    const payload = {
      ...form,
      latitude: form.latitude.trim() ? Number(form.latitude) : undefined,
      longitude: form.longitude.trim() ? Number(form.longitude) : undefined,
    };
    try {
      if (editingId) {
        await updateClub(editingId, payload);
        setMessage(`Club "${form.name}" aktualisiert.`);
      } else {
        const id = await createClub(payload);
        setMessage(`Club "${form.name}" angelegt (ID: ${id}).`);
      }
      resetForm();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? `Fehler: ${err.message}` : "Club konnte nicht gespeichert werden.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(c: Club) {
    if (!confirm(`"${c.name}" inkl. aller News, Turniere und Meldungen wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    await deleteClub(c.id);
    if (editingId === c.id) resetForm();
    onChanged();
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="font-display text-lg font-semibold text-turf-900">Bestehende Clubs</h2>
        <ul className="mt-2 space-y-2">
          {clubs.map((c) => (
            <li key={c.id} className="card flex items-center justify-between gap-3 rounded-xl px-4 py-3">
              <div className="min-w-0">
                <p className="truncate font-semibold text-ink-900">{c.name}</p>
                <p className="truncate text-sm text-ink-400">{c.contact?.address}</p>
              </div>
              <div className="flex flex-shrink-0 gap-3 text-sm font-medium">
                <button onClick={() => startEdit(c)} className="text-turf-700 underline-offset-2 hover:underline">
                  Bearbeiten
                </button>
                <button onClick={() => handleDelete(c)} className="text-flag-600 underline-offset-2 hover:underline">
                  Löschen
                </button>
              </div>
            </li>
          ))}
          {clubs.length === 0 && <p className="text-sm text-ink-400">Noch keine Clubs angelegt.</p>}
        </ul>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-turf-900">
            {editingId ? "Club bearbeiten" : "Neuen Club anlegen"}
          </h2>
          {editingId && (
            <button onClick={resetForm} className="text-sm font-medium text-ink-400 underline-offset-2 hover:underline">
              Abbrechen
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Golfclub Odenwald" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefon">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} />
            </Field>
            <Field label="E-Mail">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
            </Field>
          </div>
          <Field label="Adresse">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputClass} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Breitengrad (Latitude)">
              <input
                value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                className={inputClass}
                placeholder="49.6819"
                inputMode="decimal"
              />
            </Field>
            <Field label="Längengrad (Longitude)">
              <input
                value={form.longitude}
                onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                className={inputClass}
                placeholder="8.8987"
                inputMode="decimal"
              />
            </Field>
          </div>
          <p className="-mt-1.5 text-xs text-ink-400">
            Für die Entfernungsanzeige in der Club-Auswahl. Am schnellsten: Club bei Google Maps suchen, Rechtsklick auf den Standort → die beiden Zahlen oben im Menü kopieren (erste Zahl = Breitengrad, zweite = Längengrad).
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Öffnungszeiten Restaurant">
              <textarea
                value={form.restaurantHours}
                onChange={(e) => setForm({ ...form, restaurantHours: e.target.value })}
                rows={3}
                className={`${inputClass} h-auto py-2`}
                placeholder={"Mo-Fr 11-21 Uhr\nSa-So 10-22 Uhr"}
              />
            </Field>
            <Field label="Öffnungszeiten Sekretariat">
              <textarea
                value={form.sekretariatHours}
                onChange={(e) => setForm({ ...form, sekretariatHours: e.target.value })}
                rows={3}
                className={`${inputClass} h-auto py-2`}
                placeholder={"Mo-Fr 9-17 Uhr\nSa 9-13 Uhr"}
              />
            </Field>
          </div>
          <Field label="Website">
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputClass} placeholder="https://…" />
          </Field>
          <Field label="Startzeitenreservierung (Link, optional)">
            <input
              value={form.teeTimeUrl}
              onChange={(e) => setForm({ ...form, teeTimeUrl: e.target.value })}
              className={inputClass}
              placeholder="https://golfclub-odenwald.de/turniere/startzeitenreservierung"
            />
          </Field>
          <Field label="Turniere (Link statt manueller Eintragung, optional)">
            <input
              value={form.eventsUrl}
              onChange={(e) => setForm({ ...form, eventsUrl: e.target.value })}
              className={inputClass}
              placeholder="https://golfclub-odenwald.de/turniere"
            />
          </Field>
          <p className="-mt-1.5 text-xs text-ink-400">
            Falls hier ein Link steht, verlinkt der "Turniere"-Reiter der App direkt dorthin statt der einzeln eingetragenen Turniere.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Green Fee">
              <input value={form.greenFee} onChange={(e) => setForm({ ...form, greenFee: e.target.value })} className={inputClass} placeholder="45 €" />
            </Field>
            <Field label="Platzreife">
              <input value={form.platzreife} onChange={(e) => setForm({ ...form, platzreife: e.target.value })} className={inputClass} placeholder="350 €" />
            </Field>
            <Field label="Turnier">
              <input value={form.turnier} onChange={(e) => setForm({ ...form, turnier: e.target.value })} className={inputClass} placeholder="auf Anfrage" />
            </Field>
          </div>
          <Field label="Preise (Link statt manueller Eingabe, optional)">
            <input
              value={form.pricesUrl}
              onChange={(e) => setForm({ ...form, pricesUrl: e.target.value })}
              className={inputClass}
              placeholder="https://golfclub-odenwald.de/club#c245"
            />
          </Field>
          <p className="-mt-1.5 text-xs text-ink-400">
            Falls hier ein Link steht, verlinkt der "Preise"-Reiter der App direkt dorthin statt der Felder oben anzuzeigen.
          </p>

          {message && <p className="rounded-lg bg-turf-100 px-3 py-2 text-sm text-turf-800">{message}</p>}
          {error && <p role="alert" className="rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">{error}</p>}

          <button type="submit" disabled={submitting} className="h-11 w-full rounded-xl bg-turf-700 font-semibold text-chalk-50 disabled:opacity-50">
            {submitting ? "Wird gespeichert…" : editingId ? "Änderungen speichern" : "Club anlegen"}
          </button>
        </form>
      </section>
    </div>
  );
}

function emptyNoticeForm() {
  return { title: "", text: "", severity: "info" as Severity };
}

function NewsPanel({ clubId }: { clubId: string }) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyNoticeForm());
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setNotices(await listNotices(clubId));
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    setEditingId(null);
    setForm(emptyNoticeForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  function startEdit(n: Notice) {
    setEditingId(n.id);
    setForm({ title: n.title, text: n.text, severity: n.severity });
    setMessage(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyNoticeForm());
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    if (editingId) {
      await updateNotice(clubId, editingId, form);
      setMessage("Mitteilung aktualisiert.");
    } else {
      await addNotice(clubId, { ...form, type: "club" });
      setMessage("Mitteilung veröffentlicht.");
    }
    resetForm();
    await refresh();
    setSubmitting(false);
  }

  async function handleDelete(n: Notice) {
    if (!confirm(`Mitteilung "${n.title}" löschen?`)) return;
    await deleteNotice(clubId, n.id);
    if (editingId === n.id) resetForm();
    await refresh();
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="font-display text-lg font-semibold text-turf-900">Veröffentlichte News</h2>
        {loading && <p className="mt-2 text-sm text-ink-400">Wird geladen…</p>}
        <ul className="mt-2 space-y-2">
          {!loading && notices.length === 0 && <p className="text-sm text-ink-400">Noch keine News.</p>}
          {notices.map((n) => (
            <li key={n.id} className="card rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-ink-400">{n.severity}</p>
                  <p className="font-semibold text-ink-900">{n.title}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-ink-600">{n.text}</p>
                </div>
                <div className="flex flex-shrink-0 gap-3 text-sm font-medium">
                  <button onClick={() => startEdit(n)} className="text-turf-700 underline-offset-2 hover:underline">
                    Bearbeiten
                  </button>
                  <button onClick={() => handleDelete(n)} className="text-flag-600 underline-offset-2 hover:underline">
                    Löschen
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-turf-900">
            {editingId ? "Mitteilung bearbeiten" : "Neue Mitteilung"}
          </h2>
          {editingId && (
            <button onClick={resetForm} className="text-sm font-medium text-ink-400 underline-offset-2 hover:underline">
              Abbrechen
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <Field label="Titel">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Text">
            <textarea required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className={`${inputClass} h-auto py-2`} />
          </Field>
          <Field label="Wichtigkeit">
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value as Severity })} className={inputClass}>
              <option value="info">Info</option>
              <option value="warn">Warnung</option>
              <option value="danger">Dringend</option>
            </select>
          </Field>
          {message && <p className="rounded-lg bg-turf-100 px-3 py-2 text-sm text-turf-800">{message}</p>}
          <button type="submit" disabled={submitting} className="h-11 w-full rounded-xl bg-turf-700 font-semibold text-chalk-50 disabled:opacity-50">
            {submitting ? "Wird gespeichert…" : editingId ? "Änderungen speichern" : "Veröffentlichen"}
          </button>
        </form>
      </section>
    </div>
  );
}

function emptyEventForm() {
  return { title: "", date: "", description: "", registrationUrl: "" };
}

function toLocalInputValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventsPanel({ clubId }: { clubId: string }) {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyEventForm());
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setEvents(await listEvents(clubId));
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    setEditingId(null);
    setForm(emptyEventForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  function startEdit(ev: EventRow) {
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      date: toLocalInputValue(ev.date),
      description: ev.description,
      registrationUrl: ev.registrationUrl ?? "",
    });
    setMessage(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyEventForm());
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    const payload = {
      title: form.title,
      date: new Date(form.date).getTime(),
      description: form.description,
      registrationUrl: form.registrationUrl || undefined,
    };
    if (editingId) {
      await updateEvent(clubId, editingId, payload);
      setMessage("Turnier aktualisiert.");
    } else {
      await addEvent(clubId, payload);
      setMessage("Turnier veröffentlicht.");
    }
    resetForm();
    await refresh();
    setSubmitting(false);
  }

  async function handleDelete(ev: EventRow) {
    if (!confirm(`Turnier "${ev.title}" löschen?`)) return;
    await deleteEvent(clubId, ev.id);
    if (editingId === ev.id) resetForm();
    await refresh();
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="font-display text-lg font-semibold text-turf-900">Veröffentlichte Turniere</h2>
        {loading && <p className="mt-2 text-sm text-ink-400">Wird geladen…</p>}
        <ul className="mt-2 space-y-2">
          {!loading && events.length === 0 && <p className="text-sm text-ink-400">Noch keine Turniere.</p>}
          {events.map((ev) => (
            <li key={ev.id} className="card rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-ink-400">
                    {new Date(ev.date).toLocaleString("de-DE", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="font-semibold text-ink-900">{ev.title}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-ink-600">{ev.description}</p>
                </div>
                <div className="flex flex-shrink-0 gap-3 text-sm font-medium">
                  <button onClick={() => startEdit(ev)} className="text-turf-700 underline-offset-2 hover:underline">
                    Bearbeiten
                  </button>
                  <button onClick={() => handleDelete(ev)} className="text-flag-600 underline-offset-2 hover:underline">
                    Löschen
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-turf-900">
            {editingId ? "Turnier bearbeiten" : "Neues Turnier"}
          </h2>
          {editingId && (
            <button onClick={resetForm} className="text-sm font-medium text-ink-400 underline-offset-2 hover:underline">
              Abbrechen
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <Field label="Titel">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Datum & Uhrzeit">
            <input required type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Beschreibung">
            <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-auto py-2`} />
          </Field>
          <Field label="Anmeldungs-Link (optional)">
            <input value={form.registrationUrl} onChange={(e) => setForm({ ...form, registrationUrl: e.target.value })} className={inputClass} placeholder="https://…" />
          </Field>
          {message && <p className="rounded-lg bg-turf-100 px-3 py-2 text-sm text-turf-800">{message}</p>}
          <button type="submit" disabled={submitting} className="h-11 w-full rounded-xl bg-turf-700 font-semibold text-chalk-50 disabled:opacity-50">
            {submitting ? "Wird gespeichert…" : editingId ? "Änderungen speichern" : "Veröffentlichen"}
          </button>
        </form>
      </section>
    </div>
  );
}

const statusOptions: IssueStatus[] = ["neu", "in bearbeitung", "erledigt"];

function IssuesPanel({ clubId }: { clubId: string }) {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setIssues(await listIssues(clubId));
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  async function handleStatusChange(issueId: string, status: IssueStatus) {
    await updateIssueStatus(clubId, issueId, status);
    setIssues((prev) => prev.map((i) => (i.id === issueId ? { ...i, status } : i)));
  }

  async function handleDelete(issue: Issue) {
    if (!confirm("Diese Meldung inkl. Fotos wirklich löschen? Das kann nicht rückgängig gemacht werden.")) return;
    await deleteIssue(clubId, issue.id!, issue.photoURLs);
    setIssues((prev) => prev.filter((i) => i.id !== issue.id));
  }

  if (loading) return <p className="text-sm text-ink-400">Wird geladen…</p>;
  if (issues.length === 0) return <p className="text-sm text-ink-400">Keine Meldungen vorhanden.</p>;

  return (
    <>
      <ul className="space-y-3">
        {issues.map((issue) => (
          <li key={issue.id} className="card rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-400">{issue.category}</p>
                <p className="mt-0.5 text-ink-900">{issue.text}</p>
                <p className="mt-1 text-xs text-ink-400">
                  {issue.userName} · {new Date(issue.createdAt).toLocaleString("de-DE")}
                </p>
              </div>
              <select
                value={issue.status}
                onChange={(e) => handleStatusChange(issue.id!, e.target.value as IssueStatus)}
                className="h-9 rounded-lg border border-chalk-200 bg-white px-2 text-sm"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-2 text-right">
              <button
                onClick={() => handleDelete(issue)}
                className="text-sm font-medium text-flag-600 underline-offset-2 hover:underline"
              >
                Löschen
              </button>
            </div>
            {issue.photoURLs.length > 0 && (
              <div className="mt-3 flex gap-2">
                {issue.photoURLs.map((url) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setLightbox(url)}
                    className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg transition-opacity hover:opacity-80"
                  >
                    <img src={url} alt="Foto zur Meldung, zum Vergrößern antippen" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Foto-Großansicht"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/80 p-6"
        >
          <button
            type="button"
            aria-label="Schließen"
            onClick={() => setLightbox(null)}
            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-chalk-50"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <img
            src={lightbox}
            alt="Foto zur Meldung, vergrößert"
            className="max-h-full max-w-full rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
